
import React from 'react';
import { User } from 'lucide-react';
import { getTier, getTierDetails } from '../services/gamificationService';

interface Props {
  url?: string | null;
  xp: number;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showBadge?: boolean;
  isPremium?: boolean;
  isVip?: boolean;
  showLabel?: boolean;
  onClick?: () => void;
  disablePulsing?: boolean;
}

export const Avatar: React.FC<Props> = ({ 
  url, 
  xp, 
  size = 'md', 
  showBadge = false,
  isPremium = false,
  isVip = false,
  showLabel = false,
  onClick,
  disablePulsing = false
}) => {
  const tier = getTier(xp);
  const details = getTierDetails(tier);

  const sizeClasses = {
    sm: 'w-8 h-8 border-2',
    md: 'w-12 h-12 border-[3px]',
    lg: 'w-24 h-24 border-4',
    xl: 'w-32 h-32 border-[5px]',
  };

  const badgeSize = {
      sm: 'w-3 h-3 text-[8px]',
      md: 'w-5 h-5 text-[10px]',
      lg: 'w-8 h-8 text-sm',
      xl: 'w-10 h-10 text-base'
  };

  const labelSize = {
    sm: 'text-[6px] px-1 py-0',
    md: 'text-[8px] px-1.5 py-0.5',
    lg: 'text-[10px] px-2 py-0.5',
    xl: 'text-[12px] px-3 py-1',
  };

  // Determine label content and color
  let labelText = details.label;
  let labelBg = 'bg-slate-800';
  let labelTextCol = 'text-white';
  let labelBorder = 'border-slate-700';

  if (isVip) {
    labelText = 'VIP';
    labelBg = 'bg-purple-600';
    labelTextCol = 'text-white';
    labelBorder = 'border-purple-400';
  } else if (isPremium) {
    labelText = 'ELITE';
    labelBg = 'bg-amber-500';
    labelTextCol = 'text-slate-950';
    labelBorder = 'border-amber-300';
  } else {
    // Standard tier labels
    labelBg = details.labelBg || 'bg-slate-700';
    labelTextCol = 'text-white';
    labelBorder = `border-white/20`;
  }

  const isPulsing = !disablePulsing && (tier === 'OURO' || details.categoria !== 'Base');

  return (
    <div className="relative inline-block" onClick={onClick}>
      {/* Moldura / Frame */}
      <div 
        className={`
          rounded-full p-[2px] 
          ${sizeClasses[size]} 
          ${details.border} 
          ${isPulsing ? 'animate-pulse' : ''}
          ${details.shadow}
          bg-slate-900 overflow-hidden flex items-center justify-center
        `}
      >
        {url ? (
          <img 
            src={url} 
            alt="Avatar" 
            className="w-full h-full rounded-full object-cover" 
          />
        ) : (
          <div className="w-full h-full bg-slate-800 flex items-center justify-center">
            <User className={`text-slate-500 ${size === 'sm' ? 'w-4' : 'w-1/2'}`} />
          </div>
        )}
      </div>

      {/* Tier Badge Icon */}
      {showBadge && !showLabel && (
        <div className={`absolute -bottom-1 -right-1 bg-slate-900 rounded-full border border-slate-700 flex items-center justify-center ${badgeSize[size]} shadow-sm z-10`}>
            <span>{details.icon}</span>
        </div>
      )}

      {/* Label Badge (VIP/ELITE/TIER) */}
      {showLabel && (
        <div className={`absolute -bottom-2 left-1/2 -translate-x-1/2 ${labelBg} ${labelTextCol} ${labelBorder} border rounded-full font-black uppercase shadow-lg z-20 whitespace-nowrap ${labelSize[size]}`}>
          {labelText}
        </div>
      )}
    </div>
  );
};
